import { isDefaultRow } from "../../../utils/unitPrice";

/**
 * LIVE MARKETPLACE PREVIEW — renders a plan exactly as the end user sees it on
 * the app marketplace product page. Mirrors the T1/T2/T3 end-user card read
 * shape so it matches production:
 *   - name, interval label
 *   - the DEFAULT tier's Marketplace_price + currency symbol
 *   - savings = Industry_price − Marketplace_price (only when industry > mp)
 *   - plan highlights, trial badge, Buy / Contact button
 * Pure presentation — reads form state, no mutations.
 */

const CURRENCY_SYMBOL = { INR: "₹", USD: "$" };

const intervalLabel = (interval) => {
  if (!interval) return "";
  if (interval.interval === "lifetime") return "No Expiry";
  const n = interval.n_interval;
  const unit = interval.interval;
  if (!n && !unit) return "";
  return `every ${n} ${unit}${Number(n) > 1 ? "s" : ""}`;
};

export default function MarketplacePreview({ plan, interval, template }) {
  if (!plan) {
    return (
      <div style={styles.empty}>
        <p style={{ margin: 0 }}>Live marketplace preview</p>
        <span style={{ fontSize: 12, color: "#8a94a6" }}>
          Fill in the plan to see how customers will view it.
        </span>
      </div>
    );
  }

  const isContact = plan.price_type === "contact";
  const symbol = CURRENCY_SYMBOL[plan.currency] || "";
  const defaultRow =
    (plan.unit_ids || []).find(isDefaultRow) || (plan.unit_ids || [])[0];
  const mp = Number(defaultRow?.Marketplace_price);
  const industry = Number(defaultRow?.Industry_price);
  const hasPrice = Number.isFinite(mp);
  const showSaving =
    Number.isFinite(mp) && Number.isFinite(industry) && industry > mp;
  const savingPct = showSaving
    ? Math.round(((industry - mp) / industry) * 100)
    : 0;

  const tags = Array.isArray(plan.tags)
    ? plan.tags.filter(Boolean)
    : String(plan.tags ?? "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

  const trial = Number(plan.trial_period_days);

  return (
    <div style={styles.wrap}>
      <div style={styles.previewLabel}>Marketplace preview</div>
      <div style={styles.card}>
        {tags.length > 0 && (
          <div style={styles.tagRow}>
            {tags.map((t, i) => (
              <span key={i} style={styles.tag}>
                {t}
              </span>
            ))}
          </div>
        )}
        <div style={styles.name}>{plan.name || "Plan name"}</div>
        <div style={styles.desc}>{plan.description || "Plan description"}</div>

        {isContact ? (
          <div style={styles.contactBox}>
            <div style={styles.contactText}>Custom pricing</div>
          </div>
        ) : (
          <div style={styles.priceBlock}>
            <div style={styles.priceRow}>
              <span style={styles.price}>
                {symbol}
                {hasPrice ? mp : "0"}
              </span>
              <span style={styles.per}>
                {template === "credit" ? " / unit" : " / unit"}
                {interval ? ` · ${intervalLabel(interval)}` : ""}
              </span>
            </div>
            {showSaving && (
              <div style={styles.savingRow}>
                <span style={styles.strike}>
                  {symbol}
                  {industry}
                </span>
                <span style={styles.saveBadge}>Save {savingPct}%</span>
              </div>
            )}
          </div>
        )}

        {trial > 0 && !isContact && (
          <div style={styles.trialBadge}>{trial}-day free trial</div>
        )}

        {Array.isArray(plan.plan_highlight) &&
          plan.plan_highlight.length > 0 && (
            <ul style={styles.highlights}>
              {plan.plan_highlight.map((h, i) => (
                <li key={i} style={styles.hli}>
                  <span style={styles.check}>✓</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}

        <button type="button" style={styles.cta} disabled>
          {isContact ? "Contact us" : "Buy now"}
        </button>
        {plan.status && plan.status !== "Active" && (
          <div style={styles.statusNote}>
            {plan.status === "Out Of Stock"
              ? "Shown but not buyable (Out of Stock)"
              : "Hidden from storefront (Inactive)"}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  // Normal document flow: each preview card stacks inside its column. (Was
  // position:sticky/top:8 — with several previews nested inside the already
  // sticky .priceStepPreview column they all pinned to the same offset and
  // piled on top of one another, overlapping the form + sidebar.)
  wrap: { position: "relative" },
  previewLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#8a94a6",
    marginBottom: 8,
  },
  empty: {
    border: "1px dashed #d4d7e0",
    borderRadius: 14,
    padding: 24,
    textAlign: "center",
    color: "#6b7280",
  },
  card: {
    border: "1px solid #e6e6ee",
    borderRadius: 16,
    padding: 20,
    background: "#fff",
    boxShadow: "0 6px 24px rgba(17,24,39,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  tagRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  tag: {
    fontSize: 11,
    fontWeight: 600,
    color: "#4f46e5",
    background: "#eef0ff",
    borderRadius: 999,
    padding: "2px 10px",
  },
  name: { fontSize: 20, fontWeight: 800, color: "#111827" },
  desc: { fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  priceBlock: { marginTop: 4 },
  priceRow: { display: "flex", alignItems: "baseline", gap: 4, flexWrap: "wrap" },
  price: { fontSize: 30, fontWeight: 800, color: "#111827" },
  per: { fontSize: 13, color: "#6b7280" },
  savingRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 4 },
  strike: { fontSize: 14, color: "#9ca3af", textDecoration: "line-through" },
  saveBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: "#047857",
    background: "#d1fae5",
    borderRadius: 999,
    padding: "2px 10px",
  },
  contactBox: {
    marginTop: 4,
    padding: "10px 0",
  },
  contactText: { fontSize: 20, fontWeight: 800, color: "#111827" },
  trialBadge: {
    alignSelf: "flex-start",
    fontSize: 12,
    fontWeight: 600,
    color: "#b45309",
    background: "#fef3c7",
    borderRadius: 8,
    padding: "3px 10px",
  },
  highlights: {
    listStyle: "none",
    margin: "6px 0 0",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  hli: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 13,
    color: "#374151",
  },
  check: { color: "#10b981", fontWeight: 800 },
  cta: {
    marginTop: 8,
    border: "none",
    borderRadius: 10,
    background: "#4f46e5",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    padding: "10px 14px",
    cursor: "default",
  },
  statusNote: {
    fontSize: 11,
    color: "#9ca3af",
    textAlign: "center",
  },
};
